import { ElementType, FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Route, Switch } from 'react-router-dom';

import { AccountClosureIneligibilityReason } from '__generated__/GQL';
import useCurrentPath from 'common/hooks/useCurrentPath';
import Loader from 'components/Loader';

import AccountingExport from './AccountingExport';
import BankAccountNotEmpty from './BankAccountNotEmpty';
import Confirmation from './Confirmation';
import ConfirmationCode from './ConfirmationCode';
import ContactSupport from './ContactSupport';
import CutCards from './CutCards';
import useIsAccountClosable from './hooks/useIsAccountClosable';
import { useSessionStoredFormValues } from './hooks/useSessionStoredFormValues';
import NotAccountHolder from './NotAccountHolder';
import PendingTransactions from './PendingTransactions';
import Reasons from './Reasons';
import { getFormDefaultValues } from './Reasons/helpers';
import SecurityWarning from './SecurityWarning';
import { AccountClosureFormValues } from './types';

const ineligibilityReasonComponent = new Map<
  AccountClosureIneligibilityReason,
  ElementType
>([
  [AccountClosureIneligibilityReason.IsNotAccountHolder, NotAccountHolder],
  [AccountClosureIneligibilityReason.PendingTransaction, PendingTransactions],
  [AccountClosureIneligibilityReason.AccountNotEmpty, BankAccountNotEmpty],
]);

const AccountClosureRoutes: FC = () => {
  const { path } = useCurrentPath();

  const {
    ineligibilityReasons,
    isClosable,
    loading: loadingIsAccountClosable,
  } = useIsAccountClosable();

  const { getStoredFormValues } = useSessionStoredFormValues();

  const formMethods = useForm<AccountClosureFormValues>({
    defaultValues: getStoredFormValues() || getFormDefaultValues(),
  });

  if (loadingIsAccountClosable) {
    return <Loader />;
  }

  if (!isClosable) {
    for (const ineligibilityReason of ineligibilityReasons) {
      if (ineligibilityReasonComponent.has(ineligibilityReason)) {
        const IneligibilityReasonComponent = ineligibilityReasonComponent.get(
          ineligibilityReason,
        ) as ElementType;

        return <IneligibilityReasonComponent />;
      }
    }

    return <ContactSupport />;
  }

  return (
    <FormProvider {...formMethods}>
      <Switch>
        <Route
          component={ConfirmationCode}
          path={`${path}/confirmation-code`}
        />
        <Route component={Confirmation} path={`${path}/confirm`} />
        <Route component={CutCards} path={`${path}/cut-cards`} />
        <Route component={Reasons} path={`${path}/reasons`} />
        <Route component={SecurityWarning} path={`${path}/security-warning`} />
        <Route component={AccountingExport} />
      </Switch>
    </FormProvider>
  );
};

export default AccountClosureRoutes;
